import { useState } from 'react';
import { eventSummary, formatAgo, formatWhen } from './SecurityDetail';
import {
  filterByGroup,
  groupLabel,
  ipGroup,
  matchesSmartQuery,
  projectLabel,
  projectOptions,
  sourceLabel,
  statusLabel,
} from './securityLabels';
import { EventRow, GroupFilterChips } from './EventGroupGrid';

export function SecurityHeader({ title, subtitle, alert, statusText, onRefresh }) {
  return (
    <div className={`sec-hero${alert ? ' sec-hero--alert' : ''}`}>
      <div className="sec-hero__copy">
        <h1>{title}</h1>
        <p>{subtitle}</p>
        {statusText && (
          <div className={`sec-status ${alert ? 'sec-status--alert' : 'sec-status--ok'}`}>
            <span className="sec-status__dot" />
            {statusText}
          </div>
        )}
      </div>
      {onRefresh && (
        <button type="button" className="sec-refresh" onClick={onRefresh} style={{ marginLeft: 0 }}>
          Refresh
        </button>
      )}
    </div>
  );
}

export function SecurityTabs({ tabs, active, onChange }) {
  return (
    <div className="sec-toolbar">
      <div className="sec-tabs">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`sec-tab${active === item.id ? ' is-active' : ''}`}
            onClick={() => onChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function LoadingBoard() {
  return (
    <div className="sec-skeleton" aria-label="Loading">
      <div className="sec-skeleton__bar" />
      <div className="sec-skeleton__bar" />
      <div className="sec-skeleton__bar" />
    </div>
  );
}

export function KpiCard({ label, value, hint, onClick, danger }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`sec-card${onClick ? ' is-clickable' : ''}`}
    >
      <p className="sec-kpi__label">{label}</p>
      <p className={`sec-kpi__value${danger ? ' is-danger' : ''}`}>{value}</p>
      {hint && <p className="sec-kpi__hint">{hint}</p>}
    </Tag>
  );
}

export function ProjectCard({ source, onOpen }) {
  const urgent = source.critical_unnotified || 0;
  const inactive = Boolean(source.inactive);
  return (
    <button
      type="button"
      onClick={() => {
        if (inactive) return;
        onOpen(source.source);
      }}
      className={`sec-card sec-project${source.error ? ' is-offline' : ''}${inactive ? ' is-inactive' : ''}`}
      disabled={inactive}
    >
      <div className="sec-project__top">
        <h3>{projectLabel(source.source)}</h3>
        <span className={`sec-badge ${inactive ? 'sec-badge--warn' : source.error ? 'sec-badge--alert' : 'sec-badge--ok'}`}>
          {inactive ? 'Inactive' : source.error ? 'Offline' : 'Connected'}
        </span>
      </div>
      {inactive && <p className="sec-row__meta">VPS not connected</p>}
      {source.error && !inactive && <p className="sec-row__meta">{source.error}</p>}
      {!source.error && !inactive && (
        <div className="sec-stat-row">
          <div className="sec-stat">
            <strong style={{ color: urgent ? '#b91c1c' : '#0f0f1a' }}>{urgent}</strong>
            <span>Urgent</span>
          </div>
          <div className="sec-stat">
            <strong>{source.ip_counter_total || 0}</strong>
            <span>Addresses</span>
          </div>
          <div className="sec-stat">
            <strong>{source.monitored_active || 0}</strong>
            <span>Watch list</span>
          </div>
        </div>
      )}
      <p className="sec-link">{inactive ? 'Coming soon' : 'Open project →'}</p>
    </button>
  );
}

export function SmartFilter({ value, onChange, placeholder }) {
  return (
    <div className="sec-search">
      <svg className="sec-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3-3" />
      </svg>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function EventList({ events, group, onOpen, onFilter }) {
  const [query, setQuery] = useState('');
  const rows = filterByGroup(events, group, 'event').filter((row) =>
    matchesSmartQuery(row, query, [eventSummary(row)]),
  );
  return (
    <div className="animate-fadeIn">
      <GroupFilterChips active={group} onChange={onFilter} allLabel="All events" />
      <SmartFilter value={query} onChange={setQuery} placeholder="Search events — IP, who, path, urgent…" />
      <div className="sec-list">
        {rows.length === 0 && (
          <div className="sec-empty">
            <strong>Nothing here</strong>
            No security events match this view.
          </div>
        )}
        {rows.map((row) => (
          <EventRow
            key={`${row.backend}-${row.id}`}
            row={row}
            onOpen={() => onOpen(row)}
          />
        ))}
      </div>
    </div>
  );
}

export function IpList({ ips, group, alertAt, onOpen, onFilter }) {
  const [query, setQuery] = useState('');
  const rows = filterByGroup(ips, group, 'ip').filter((row) => matchesSmartQuery(row, query));
  return (
    <div className="animate-fadeIn">
      <GroupFilterChips
        active={group}
        onChange={onFilter}
        allLabel="All addresses"
        kinds={['rate_limit', 'ip_block', 'ai']}
      />
      <SmartFilter value={query} onChange={setQuery} placeholder="Search addresses — IP, project, rate limit, AI…" />
      <div className="sec-list">
        {rows.length === 0 && (
          <div className="sec-empty">
            <strong>No addresses</strong>
            No blocked addresses match this view.
          </div>
        )}
        {rows.map((row) => (
          <button
            key={`${row.backend}-${row.id}`}
            type="button"
            onClick={() => onOpen(row)}
            className={`sec-row${row.block_count >= alertAt ? ' is-hot' : ''}`}
          >
            <div className="sec-row__top">
              <strong>{row.ip}</strong>
              <span className="sec-row__when" title={formatWhen(row.last_blocked_at)}>{formatAgo(row.last_blocked_at)}</span>
            </div>
            <p className="sec-row__meta">
              {projectLabel(row.backend)} · {sourceLabel(row.source) || groupLabel(ipGroup(row))} · {row.block_count} blocks
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function MonitoredList({ rows, onOpen, onRevert }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('active');
  const filtered = rows.filter((row) => {
    if (status && row.status !== status) return false;
    return matchesSmartQuery(row, query);
  });
  return (
    <div className="animate-fadeIn">
      <div className="sec-chips">
        {[
          { id: 'active', label: 'Active' },
          { id: '', label: 'Everyone' },
        ].map((chip) => (
          <button
            key={chip.id || 'all'}
            type="button"
            className={`sec-chip${status === chip.id ? ' is-active' : ''}`}
            onClick={() => setStatus(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <SmartFilter value={query} onChange={setQuery} placeholder="Search watch list — who, IP, reason, project…" />
      <div className="sec-list">
        {filtered.length === 0 && (
          <div className="sec-empty">
            <strong>No matches</strong>
            No one matches this filter.
          </div>
        )}
        {filtered.map((row) => (
          <div key={`${row.backend}-${row.id}`} className="sec-watch-row">
            <button
              type="button"
              onClick={() => onOpen(row)}
              className="sec-row"
              style={{ padding: 0, border: 0, flex: 1 }}
            >
              <div className="sec-row__top">
                <strong>{row.actor_key || `user ${row.user_id}`}</strong>
                <span className={`sec-badge ${row.status === 'active' ? 'sec-badge--warn' : 'sec-badge--muted'}`}>
                  {statusLabel(row.status)}
                </span>
              </div>
              <p className="sec-row__meta">
                {projectLabel(row.backend)} · {row.reason || '—'} · until {formatWhen(row.expires_at)}
              </p>
            </button>
            {row.status === 'active' && (
              <button type="button" className="sec-ghost" onClick={() => onRevert(row.backend, row.id)}>
                Return to normal
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MonitorForm({
  actorKey,
  setActorKey,
  monitorSource,
  setMonitorSource,
  sources,
  saving,
  onSubmit,
  lockSource,
}) {
  const options = projectOptions(sources);
  return (
    <form onSubmit={onSubmit} className="sec-card sec-form">
      <h3>Add someone to the watch list</h3>
      <input
        value={actorKey}
        onChange={(e) => setActorKey(e.target.value)}
        placeholder="Who — IP or account key"
      />
      <div className="sec-form__row">
        {!lockSource && (
          <select value={monitorSource} onChange={(e) => setMonitorSource(e.target.value)}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        )}
        <button type="submit" disabled={saving} className="sec-primary">
          {saving ? 'Saving…' : 'Watch'}
        </button>
      </div>
    </form>
  );
}
